/**
 * mobile-channels-sheet.tsx — Hộp "Tài khoản kênh" (trượt từ đáy) của Hộp thư di động, mở bằng nút mở rộng ở ô thứ 6 của hàng kênh.
 * HAI CHẾ ĐỘ:
 *   • Chọn (mặc định khi mở): danh sách ĐẦY ĐỦ tài khoản kênh (ảnh · tên · nền tảng · dấu đang lọc). Chạm một dòng = lọc theo kênh đó rồi đóng hộp.
 *     Không có tay cầm / mũi tên — chỉ để xem và chọn. Nút "Sắp xếp" ở đầu hộp mới chuyển sang chế độ sắp xếp.
 *   • Sắp xếp: đổi thứ tự bằng KÉO-THẢ (tay cầm bên trái, ngón tay / chuột) hoặc nút lên / xuống (bàn phím, trợ năng); vạch ngăn cho biết
 *     `ROW_CHANNELS` kênh đầu là các kênh hiện ở đầu trang; nút "Mặc định" về thứ tự gốc; "Xong" quay lại chế độ Chọn. Chạm dòng không lọc.
 * Thứ tự lưu ngay mỗi lần đổi (theo người dùng, đồng bộ nhiều thiết bị — hooks/use-mobile-channels.ts). Mở lại hộp luôn bắt đầu ở chế độ Chọn.
 * Kéo-thả dùng chung bộ máy của hộp "Sửa phân đoạn" (`useDragReorder` — pointer events, `touch-action: none` CHỈ trên tay cầm → vẫn cuộn được hộp).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, GripVertical, RotateCcw } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { PlatformBadge } from '@/components/shared/platform-mark'
import { useDragReorder } from './mobile-segments-sheet'
import { ROW_CHANNELS, hasExpand, moveAccountId } from '@/lib/mobile-channels'
import { shortUnreadCount } from '@/lib/inbox-account'
import type { ChannelAccount } from '@/hooks/use-integrations'
import { platformLabelOf } from '@/lib/platform-labels'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { cn, initials } from '@/lib/utils'

export function ChannelsSheet({ open, onOpenChange, accounts, accountId, unreadOf, onPick, onReorder, onReset, isDefault }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** Đã sắp theo thứ tự người dùng. */
  accounts: ChannelAccount[]
  accountId?: string
  /** Số chưa đọc của một tài khoản khác tài khoản đang chọn (chế độ Chọn hiện huy hiệu; 0 / bỏ trống = không). */
  unreadOf?: (id: string) => number
  onPick: (id?: string) => void
  /** Thứ tự mới (mã tài khoản, đủ mọi tài khoản đang có). */
  onReorder: (ids: string[]) => void
  onReset: () => void
  isDefault: boolean
}) {
  const { t } = useTranslation('convList')
  const [sorting, setSorting] = useState(false)
  // Đóng hộp (chọn kênh / vuốt xuống / "Đóng") → lần mở sau luôn bắt đầu ở chế độ Chọn.
  useEffect(() => { if (!open) setSorting(false) }, [open])

  const ids = accounts.map((a) => a.id)
  const drag = useDragReorder(ids, onReorder)
  const split = accounts.length > ROW_CHANNELS
  const canSort = hasExpand(accounts.length)

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={t('chSheet.title')}>
        <div className="pb-3 pt-2">
          <div className="flex items-center justify-between px-4 pb-1">
            <h2 className="text-base font-semibold">{sorting ? t('chSheet.sortTitle') : t('chSheet.title')}</h2>
            {sorting ? (
              <button
                type="button"
                onClick={onReset}
                disabled={isDefault}
                className="inline-flex h-9 items-center gap-1 rounded-full px-2.5 text-sm text-muted-foreground active:bg-muted disabled:opacity-40"
              >
                <RotateCcw className="h-3.5 w-3.5" /> {t('shared.default')}
              </button>
            ) : canSort && (
              <button
                type="button"
                onClick={() => setSorting(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-sm font-medium text-primary active:bg-muted"
              >
                <ArrowUpDown className="h-4 w-4" /> {t('common:actions.sort')}
              </button>
            )}
          </div>
          <p className="px-4 pb-2 text-xs text-muted-foreground">
            {sorting
              ? (split ? t('chSheet.sortHintSplit', { count: ROW_CHANNELS }) : t('chSheet.sortHint'))
              : t('chSheet.pickHint')}
          </p>

          {/* "Tất cả" không xếp thứ tự được → chỉ ở chế độ Chọn. */}
          {!sorting && (
            <ul className="divide-y border-y">
              <li>
                <button type="button" onClick={() => onPick(undefined)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left active:bg-muted">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary"><img src="/assets/logo-mark.png" alt="" className="h-6 w-6 object-contain" /></span>
                  <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{t('chSheet.allAccounts')}</span>
                  {accountId === undefined && <Check className="h-5 w-5 shrink-0 text-primary" />}
                </button>
              </li>
            </ul>
          )}

          <ul ref={drag.listRef} className={cn('relative divide-y', !sorting && 'border-b')} aria-label={sorting ? t('chSheet.sortedAria') : t('chSheet.listAria')}>
            {accounts.map((a, i) => {
              const name = a.displayName || platformLabelOf(a.platform)
              const src = avatarSrc(a.avatarUrl)
              const dragging = sorting && drag.active?.id === a.id
              const picked = accountId === a.id
              const info = (
                <>
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary">
                    <Avatar className="h-full w-full">{src && <AvatarImage src={src} alt={name} />}<AvatarFallback className="text-xs">{initials(name)}</AvatarFallback></Avatar>
                    <PlatformBadge platform={a.platform} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium">{name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{platformLabelOf(a.platform)}</span>
                  </span>
                </>
              )
              return [
                // Vạch ngăn "hiện ở đầu trang" / "chỉ trong danh sách này" — chỉ ở chế độ Sắp xếp, đứng theo VỊ TRÍ (không đi theo dòng đang kéo).
                sorting && split && i === ROW_CHANNELS ? (
                  <li key="sep" role="presentation" className="bg-muted/50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('chSheet.onlyInList')}
                  </li>
                ) : null,
                <li
                  key={a.id}
                  ref={drag.rowRef(a.id)}
                  className={cn(dragging && 'relative z-10 bg-background shadow-lg ring-1 ring-primary/30')}
                  style={dragging ? { transform: `translateY(${drag.active!.dy}px)` } : undefined}
                >
                  {sorting ? (
                    <div className="flex items-center gap-1 pr-2">
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={t('segSheet.dragAria', { label: name })}
                        {...drag.grip(a.id)}
                        // touch-action: none CHỈ ở tay cầm — vùng còn lại của hộp vẫn cuộn bằng ngón tay.
                        style={{ touchAction: 'none' }}
                        className={cn('ml-2 flex h-11 w-9 shrink-0 cursor-grab items-center justify-center rounded-full text-muted-foreground active:cursor-grabbing', dragging && 'text-primary')}
                      >
                        <GripVertical className="h-5 w-5" />
                      </button>
                      <div className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 pr-1">{info}</div>
                      <button type="button" aria-label={t('segSheet.moveUp', { label: name })} disabled={i === 0} onClick={() => onReorder(moveAccountId(ids, a.id, -1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                        <ArrowUp className="h-5 w-5" />
                      </button>
                      <button type="button" aria-label={t('segSheet.moveDown', { label: name })} disabled={i === accounts.length - 1} onClick={() => onReorder(moveAccountId(ids, a.id, 1))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted disabled:opacity-25">
                        <ArrowDown className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onPick(a.id)}
                      aria-pressed={picked}
                      className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left active:bg-muted"
                    >
                      {info}
                      {(unreadOf?.(a.id) ?? 0) > 0 && (
                        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-none text-destructive-foreground" aria-label={t('shared.unreadCount', { count: unreadOf?.(a.id) ?? 0 })}>
                          {shortUnreadCount(unreadOf?.(a.id) ?? 0)}
                        </span>
                      )}
                      {picked && <Check className="h-5 w-5 shrink-0 text-primary" />}
                    </button>
                  )}
                </li>,
              ]
            })}
            {/* Vạch chỉ chỗ sẽ thả */}
            {sorting && drag.active && drag.active.to !== drag.active.from && (
              <li aria-hidden data-testid="drop-line" className="pointer-events-none absolute inset-x-4 z-20 h-0.5 -translate-y-px rounded-full bg-primary" style={{ top: drag.active.lineY }} />
            )}
          </ul>

          <div className="px-4 pt-3">
            <button
              type="button"
              onClick={() => (sorting ? setSorting(false) : onOpenChange(false))}
              className="h-11 w-full rounded-full bg-primary text-[15px] font-medium text-primary-foreground active:opacity-90"
            >
              {sorting ? t('common:actions.done') : t('common:actions.close')}
            </button>
          </div>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  )
}
